import React from "react";

/**
 * Polaris-free onboarding skeleton for app boot / PersistGate / Suspense
 * (no AppProvider required). Layout mirrors the loaded overview:
 * banner + shop chip + logo row, welcome + 3-step stepper, connect card, pricing card.
 */

const SHIMMER = "#e1e3e5";
const SURFACE = "#ffffff";
const PAGE_BG = "#f6f6f7";
const CARD_SECONDARY = "#f6f6f7";

const TOKENS = {
  pageMaxWidthPx: 980,
  bannerHeightPx: 160,
  avatarSizePx: 48,
  shopChipMinWidthPx: 280,
  logoTileSizePx: 56,
  logoTileCenterSizePx: 72,
  stepCircleSizePx: 26,
  taskOutlineSizePx: 20,
  connectButtonWidthPx: 140,
  connectButtonHeightPx: 36,
  contentPaddingPx: 16,
} as const;

const ShimmerBar = ({
  width,
  height = 14,
  radius = 6,
}: {
  width: number | string;
  height?: number;
  radius?: number;
}) => (
  <div
    style={{
      width,
      height,
      borderRadius: radius,
      backgroundColor: SHIMMER,
    }}
  />
);

const DashedCircle = ({ size }: { size: number }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      border: "2px dashed #c9cccf",
      boxSizing: "border-box",
      flexShrink: 0,
    }}
  />
);

const useIsMobileLayout = (): boolean => {
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return isMobile;
};

function BannerSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  const logoTiles = [
    { size: TOKENS.logoTileSizePx, key: "shopify" },
    { size: TOKENS.logoTileCenterSizePx, key: "ez" },
    { size: TOKENS.logoTileSizePx, key: "etsy" },
  ] as const;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: TOKENS.bannerHeightPx,
        background: SHIMMER,
        overflow: "hidden",
      }}
    >
      {/* Shop profile chip — top left */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          display: "flex",
          alignItems: "center",
          gap: 12,
          minWidth: `min(${TOKENS.shopChipMinWidthPx}px, calc(100% - 24px))`,
          background: SURFACE,
          borderRadius: 8,
          padding: 10,
          boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
        }}
      >
        <div
          style={{
            width: TOKENS.avatarSizePx,
            height: TOKENS.avatarSizePx,
            borderRadius: "50%",
            background: SHIMMER,
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <ShimmerBar width="72%" height={14} />
          <div style={{ marginTop: 8 }}>
            <ShimmerBar width="92%" height={12} />
          </div>
        </div>
      </div>

      {/* Integration logo row — center-right (Shopify → EZ → Etsy); desktop only */}
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            right: 24,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {logoTiles.map((tile, index) => (
            <React.Fragment key={tile.key}>
              {index > 0 && (
                <div
                  style={{
                    width: 18,
                    height: 2,
                    borderTop: "2px dashed rgba(255,255,255,0.7)",
                    flexShrink: 0,
                  }}
                />
              )}
              <div
                style={{
                  width: tile.size,
                  height: tile.size,
                  borderRadius: 12,
                  background: SURFACE,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  flexShrink: 0,
                }}
              />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function StepperSkeleton(): JSX.Element {
  return (
    <div
      style={{
        width: "100%",
        maxWidth: 420,
        paddingTop: 4,
        paddingBottom: 4,
        position: "relative",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "16.666%",
          width: "66.666%",
          top: 20,
          height: 2,
          background: SHIMMER,
          borderRadius: 1,
          zIndex: 0,
        }}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          position: "relative",
          zIndex: 1,
        }}
      >
        {[0, 1, 2].map((step) => (
          <div
            key={step}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 0,
              paddingInline: 4,
            }}
          >
            <div
              style={{
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: SURFACE,
                borderRadius: 20,
                paddingInline: 2,
              }}
            >
              <div
                style={{
                  width: TOKENS.stepCircleSizePx,
                  height: TOKENS.stepCircleSizePx,
                  borderRadius: "50%",
                  background: SHIMMER,
                }}
              />
            </div>
            <div style={{ marginTop: 10, width: "72%" }}>
              <ShimmerBar width="100%" height={12} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WelcomeRowSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "flex-start",
        justifyContent: "space-between",
        gap: isMobile ? 16 : 12,
      }}
    >
      <div style={{ flex: isMobile ? undefined : 1, minWidth: 0 }}>
        <ShimmerBar width={isMobile ? "92%" : "78%"} height={22} />
        <div style={{ marginTop: 10 }}>
          <ShimmerBar width={isMobile ? "70%" : "48%"} height={14} />
        </div>
      </div>
      <div
        style={{
          minWidth: isMobile ? undefined : 260,
          maxWidth: isMobile ? "100%" : 440,
          width: isMobile ? "100%" : undefined,
          flexShrink: 0,
        }}
      >
        <StepperSkeleton />
      </div>
    </div>
  );
}

function ConnectCardSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  if (isMobile) {
    return (
      <div
        style={{
          padding: 16,
          background: CARD_SECONDARY,
          borderRadius: 8,
        }}
      >
        <ShimmerBar width="55%" height={18} />
        <div style={{ marginTop: 10 }}>
          <ShimmerBar width="100%" height={12} />
          <div style={{ marginTop: 8 }}>
            <ShimmerBar width="88%" height={12} />
          </div>
        </div>
        <div style={{ marginTop: 16 }}>
          <ShimmerBar width="100%" height={44} radius={8} />
        </div>
        <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
          <ShimmerBar width="min(100%, 280px)" height={12} />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 12,
        background: CARD_SECONDARY,
        borderRadius: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 20,
        }}
      >
        <div style={{ display: "flex", gap: 12, flex: 1, minWidth: 0 }}>
          <div style={{ marginTop: 2 }}>
            <DashedCircle size={TOKENS.taskOutlineSizePx} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <ShimmerBar width="42%" height={18} />
            <div style={{ marginTop: 10 }}>
              <ShimmerBar width="92%" height={12} />
              <div style={{ marginTop: 8 }}>
                <ShimmerBar width="78%" height={12} />
              </div>
            </div>
          </div>
        </div>
        <div style={{ flexShrink: 0, transform: "translateX(-65px)" }}>
          <ShimmerBar
            width={TOKENS.connectButtonWidthPx}
            height={TOKENS.connectButtonHeightPx}
            radius={8}
          />
        </div>
      </div>
      <div style={{ marginTop: 12, maxWidth: 520 }}>
        <ShimmerBar width="88%" height={12} />
      </div>
    </div>
  );
}

function PricingCardSkeleton(): JSX.Element {
  return (
    <div
      style={{
        padding: 12,
        background: CARD_SECONDARY,
        borderRadius: 8,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ marginTop: 2 }}>
          <DashedCircle size={TOKENS.taskOutlineSizePx} />
        </div>
        <div style={{ flex: 1, minWidth: 0, maxWidth: 280 }}>
          <ShimmerBar width="70%" height={16} />
        </div>
      </div>
    </div>
  );
}

/** Polaris-free onboarding skeleton for pre-AppProvider boot (FCP/LCP). */
export const OnboardingBootSkeleton: React.FC = () => {
  const isMobile = useIsMobileLayout();

  return (
    <div
      style={{
        maxWidth: TOKENS.pageMaxWidthPx,
        margin: "0 auto",
        background: PAGE_BG,
      }}
    >
      <div
        style={{
          background: SURFACE,
          border: "1px solid #e1e3e5",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <BannerSkeleton isMobile={isMobile} />

        <div style={{ padding: TOKENS.contentPaddingPx }}>
          <WelcomeRowSkeleton isMobile={isMobile} />

          <div style={{ marginTop: 16, marginBottom: 12 }}>
            <ConnectCardSkeleton isMobile={isMobile} />
          </div>

          <div style={{ marginTop: 12 }}>
            <PricingCardSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBootSkeleton;
